import { makeAutoObservable, runInAction } from "mobx";
import { toast } from "react-toastify";
import {
  createCustomReportURL,
  deleteCustomReportURL,
  getAllCNPReports,
  getCNPReportURL,
  updateCustomReportURL,
} from "../api/url";
import { CUSTOM_REPORT_INITIAL } from "../constants/cnp.constants";
import {
  buildMetadataForCustomReport,
  extractImgsFromHTML,
  getComKey,
  getCompressedBinaries,
  handleCompressedBase64Component,
  htmlToCompressedBase64,
} from "../utils/cnp.util";
import { base64ToUint8Array } from "../../../utils/utils";
import { inflate } from "pako";
import { handleFetch } from "../../../utils/handleFetch";
import { cleanupTempImages } from "../lib/MyCustomUploadAdapterPlugin";

class CNPReportStore {
  allCNPReportTemplates = [];
  templateNameList = [];
  selectedCNPTemplate = null;
  selectedCNPReport = {};
  CNPReportComponentIndex = 0;
  customReport = CUSTOM_REPORT_INITIAL;
  currentDate = new Date();
  isComponentNameEdit = false;

  constructor() {
    makeAutoObservable(this);
  }

  setIsComponentNameEdit = (v) => (this.isComponentNameEdit = v);

  setSelectedComponentName = (v) => {
    this.selectedCNPReport.componentList[this.CNPReportComponentIndex].title =
      v;
  };

  deleteSelectedComponent = (index) => {
    this.selectedCNPReport.componentList.splice(index, 1);
  };

  setSelectedCNPTemplate = (v) => {
    this.selectedCNPTemplate = v;
  };

  setCompanyLogo = (files) => {
    this.customReport["companyLogo"] = files;
  };

  getAllCNPTemplates = async () => {
    return await handleFetch({
      url: getAllCNPReports,
      method: "GET",
      onSuccess: (data) =>
        runInAction(() => {
          this.templateNameList = data;
          const findData = data?.find(
            (d) =>
              d.version === 1 && d.type === "CNP" && d.language === "English",
          );
          this.selectedCNPTemplate = findData?.key;
        }),
    });
  };

  getCNPReport = async (contents) => {
    const { isDefault, key } =
      this.templateNameList.find(
        ({ key }) => key === this.selectedCNPTemplate,
      ) || {};
    const type = isDefault ? "default" : "custom";
    return await handleFetch({
      url: getCNPReportURL(type, key),
      method: "GET",
      onSuccess: (data) =>
        runInAction(() => {
          data.componentList.forEach((component) => {
            handleCompressedBase64Component(component, contents);
          });
          data.isDefault = isDefault;
          this.selectedCNPReport = data;
        }),
    });
  };

  _handleChangeComponent = (index) => (this.CNPReportComponentIndex = index);

  setContent = (content) =>
    (this.selectedCNPReport.componentList[
      this.CNPReportComponentIndex
    ].content = content);

  handleSaveCNPContent = async () => {
    let bodyData = {
      ...this.selectedCNPReport,
      componentList: this.selectedCNPReport.componentList?.map((component) => ({
        ...component,
      })),
    };
    let newImageFiles = {};
    let existingImageFiles = {};

    bodyData.componentList?.forEach((component) => {
      const newPath = `report/${bodyData._id}/${getComKey(component.name)}`;
      const { newFiles, existingFiles, newHtmlString } = extractImgsFromHTML(
        component.content,
        newPath,
      );

      if (newFiles?.length > 0) {
        newImageFiles[`uploads/${newPath}/`] = newFiles;
      }
      existingImageFiles[`uploads/${newPath}/`] = existingFiles;

      const base64 = htmlToCompressedBase64(newHtmlString);
      component.content = base64;
    });

    const body = { newImageFiles, existingImageFiles, data: bodyData };

    return await handleFetch({
      url: updateCustomReportURL,
      method: "PUT",
      body,
      onSuccess: (data, message) =>
        runInAction(() => {
          data.componentList.forEach((component) => {
            handleCompressedBase64Component(component);
          });
          data.isDefault = bodyData.isDefault;
          this.selectedCNPReport = data;
          this.errors = {};
          cleanupTempImages();
          toast.success(message);
        }),
      onValidationError: (errors) => runInAction(() => (this.errors = errors)),
    });
  };

  handleChangeCustomTemplate = (e) => {
    const { id, value } = e.target;
    this.customReport[id] = value;
  };

  handleCreateCustomTemplate = async (contentsFromStatic) => {
    const clonedCNPReport = {
      ...this.selectedCNPReport,
      componentList: this.selectedCNPReport.componentList?.map((component) => ({
        ...component,
      })),
    };
    const { isDefault, componentList, _id } = clonedCNPReport || {};
    let metadata = { ...this.customReport };

    const file = metadata?.companyLogo?.[0];
    if (file) metadata.logoFileName = file.name;

    if (isDefault) {
      const compressedBinaries = getCompressedBinaries(contentsFromStatic);
      metadata.contentsFromStatic = compressedBinaries;
    } else {
      metadata = await buildMetadataForCustomReport(
        metadata,
        componentList,
        contentsFromStatic,
        _id,
      );
    }

    const encoder = new TextEncoder();
    const metadataJson = JSON.stringify(metadata);
    const metadataBytes = encoder.encode(metadataJson);

    let body;

    if (file) {
      const fileBuffer = new Uint8Array(await file.arrayBuffer());
      body = new Uint8Array(metadataBytes.length + fileBuffer.length + 1);
      body.set(metadataBytes, 0);
      body.set([10], metadataBytes.length); // newline separator
      body.set(fileBuffer, metadataBytes.length + 1);
    } else {
      body = metadataBytes;
    }

    const headers = {
      "Content-Type": "application/octet-stream",
      "X-Metadata-Length": metadataBytes.length,
    };

    return await handleFetch({
      url: createCustomReportURL + "/" + this.selectedCNPTemplate,
      method: "POST",
      body,
      headers,
      onSuccess: (data, message) =>
        runInAction(() => {
          const newTemplate = {
            key: data._id,
            value: data.templateName,
            language: data.language,
            version: data.version,
            type: data.type,
          };
          this.selectedCNPTemplate = data._id;
          this.templateNameList.push(newTemplate);
          this.customReport = CUSTOM_REPORT_INITIAL;
          this.errors = null;
          cleanupTempImages();
          toast.success(message);
        }),
      onValidationError: (errors) => runInAction(() => (this.errors = errors)),
    });
  };

  handleDeleteCustomReport = async () => {
    return await handleFetch({
      url: deleteCustomReportURL(this.selectedCNPReport._id),
      method: "DELETE",
      onSuccess: (_, message) =>
        runInAction(() => {
          document.getElementById("deleteModalClose").click();
          this.selectedCNPTemplate = null;
          this.getAllCNPTemplates();
          toast.success(message);
        }),
    });
  };

  handleClear = () => {
    // this.selectedCNPTemplate = null;
    this.customReport = CUSTOM_REPORT_INITIAL;
    this.errors = null;
  };
}

export const cnpReportStore = new CNPReportStore();
